import { governedMcpApi } from '../governed-mcp-gateway/governedMcpApi';
import { clientLabel } from '../governed-mcp-gateway/quotaUsage';
import { llmGatewayApi } from '../llm-gateway/llmGatewayApi';
import { logOmniChat } from './omniChatLogger';
import { SYNTHETIC_GATEWAYS } from './omniChatSynthetic';
import type { OmniChatGateway, OmniChatInferenceKey } from './omniChatTypes';

export type OmniChatCatalogs = {
  gateways: OmniChatGateway[];
  inferenceKeys: OmniChatInferenceKey[];
};

export function lookupGateway(id: string, gateways: OmniChatGateway[]): OmniChatGateway | undefined {
  return gateways.find((item) => item.id === id) ?? SYNTHETIC_GATEWAYS.find((item) => item.id === id);
}

function asInferenceKeys(
  assignments: { id: string; resourceType?: string; resourceId?: string; clientId: string; hasInferenceKey?: boolean; inferenceKeyLast4?: string | null; month: string }[],
  clients: { id: string; name: string }[]
): OmniChatInferenceKey[] {
  return assignments
    .filter((item) => item.resourceType === 'llm_gateway' && item.hasInferenceKey)
    .map((item) => ({
      id: item.id,
      gatewayId: item.resourceId || item.clientId,
      clientId: item.clientId,
      clientName: clientLabel(item.clientId, clients),
      last4: item.inferenceKeyLast4 || '',
      month: item.month,
    }));
}

export async function loadOmniChatCatalogs(): Promise<{ catalogs: OmniChatCatalogs; failed: boolean }> {
  logOmniChat('app', 'catalogs_load');
  try {
    const items = await llmGatewayApi.listGateways();
    const gateways = items
      .filter((item) => item.status === 'active')
      .map((item) => ({
        id: item.id,
        name: item.name,
        model: item.publicAlias || item.provider,
      }));
    const gatewayKeys: OmniChatInferenceKey[] = items
      .filter((item) => item.hasInferenceKey)
      .map((item) => ({
        id: item.id,
        gatewayId: item.id,
        clientId: '',
        clientName: 'Gateway key',
        last4: item.inferenceKeyLast4 || '',
        month: '',
      }));
    let inferenceKeys: OmniChatInferenceKey[] = gatewayKeys;
    try {
      const [quotas, clients] = await Promise.all([governedMcpApi.listQuotas(), governedMcpApi.listClients()]);
      inferenceKeys = [...gatewayKeys, ...asInferenceKeys(quotas.assignments, clients)];
    } catch (error) {
      logOmniChat('error', 'catalogs_keys_failed', {
        message: error instanceof Error ? error.message : 'keys failed',
      });
    }
    logOmniChat('app', 'catalogs_load_ok', { gateways: gateways.length, keys: inferenceKeys.length });
    return { catalogs: { gateways, inferenceKeys }, failed: false };
  } catch (error) {
    logOmniChat('error', 'catalogs_gateways_failed', {
      message: error instanceof Error ? error.message : 'gateways failed',
    });
    return { catalogs: { gateways: [], inferenceKeys: [] }, failed: true };
  }
}
